/**
 * O cliente da API.
 *
 * ENDERECO DE SERVIDOR: NENHUM. Tudo sai por `/api/...`, e quem reencaminha e
 * o proxy do Vite em desenvolvimento e o servidor de arquivos estaticos em
 * producao. Endereco absoluto escrito no codigo do produto e o que faz uma
 * instalacao virar quatro.
 *
 * A CREDENCIAL VIVE NO `sessionStorage`, e nao no `localStorage`. A diferenca
 * importa: `sessionStorage` morre quando a aba fecha. Num sistema de contratos
 * usado em computador compartilhado de secretaria, credencial que sobrevive ao
 * fechamento do navegador e credencial que o proximo servidor herda.
 *
 * ERRO DA API NAO VIRA `Error` GENERICO. A API responde com campo, regra e
 * acao corretiva; jogar isso fora e substituir por "erro ao carregar" desfaz
 * exatamente o trabalho que o backend teve para dizer o que fazer.
 */

const CHAVE_DA_CREDENCIAL = "contratos.credencial"
const CHAVE_DA_CONTA = "contratos.conta"
const PREFIXO_DA_ULTIMA_ENTIDADE = "contratos.ultima_entidade."

export type EntidadeDaSessao = { id: number; nome: string; sigla: string; desativada_em?: string | null }

/** Um perfil como a tela o escreve: a chave, o rótulo e o que ele pode
 *  (spec 0072). Vem do servidor; a tela não guarda lista própria. */
export type PerfilNaTela = { papel: string; nome: string; pode: string }

export type Conta = {
  usuario_id: number
  nome: string
  /** O e-mail de quem entrou. A janela que assina o laudo o mostra ao lado do
   *  campo de senha — e assim o gerenciador de senhas do navegador escreve o
   *  usuario ONDE DEVE, em vez de sair procurando um campo de texto na tela.
   *
   *  Vazio numa sessao aberta antes de 09/09/2026, que e quando a entrada
   *  passou a devolve-lo: ver `contaGuardada`. */
  email: string
  /** O CPF de quem entrou, formatado (spec 0073: entra-se pelo CPF). A janela
   *  que assina o laudo o mostra ao lado da senha, para o gerenciador de senhas.
   *  Vazio numa sessão aberta antes da spec 0073: ver `contaGuardada`. */
  cpf: string
  /** O perfil de maior alcance. A lista completa está em `perfis`. */
  papel: string
  /** Todos os perfis da pessoa (spec 0069), e os mesmos com rótulo. */
  perfis: string[]
  perfis_na_tela: PerfilNaTela[]
  is_root: boolean
  is_demo: boolean
  /** A entidade DESTA sessão. Nula para o ROOT. */
  entidade: EntidadeDaSessao | null
  /** As entidades em que a pessoa tem vínculo ativo, do mais antigo ao mais
   *  novo. A troca é por `api.trocarEntidade`. Vazia para o ROOT. */
  entidades: EntidadeDaSessao[]

  /* --- o que a entrada passou a dizer sobre a senha (F37) --------------- */
  /** Verdadeiro enquanto OUTRA PESSOA escolheu a senha desta conta. */
  senha_provisoria: boolean
  senha_alterada_em: string | null
  /** A entidade pede a troca no primeiro acesso — parametro, nao constante. */
  troca_de_senha_exigida: boolean
  /** Verdadeiro so quando o parametro esta em `bloqueio`: a tela de troca nao
   *  oferece saida. Em `alerta` ela oferece, e o fato fica na trilha. */
  troca_de_senha_bloqueia: boolean
  politica_da_senha: string
}

export class ErroDaApi extends Error {
  readonly codigo: number
  readonly campo: string | null
  readonly regra: string
  readonly acao: string
  readonly referencia: string | null

  constructor(codigo: number, corpo: Record<string, unknown>) {
    const regra =
      typeof corpo["regra"] === "string"
        ? corpo["regra"]
        : "não foi possível completar a operação. Tente novamente"
    super(regra)
    this.codigo = codigo
    this.regra = regra
    this.campo = typeof corpo["campo"] === "string" ? corpo["campo"] : null
    this.acao = typeof corpo["acao"] === "string" ? corpo["acao"] : ""
    this.referencia =
      typeof corpo["referencia"] === "string" ? corpo["referencia"] : null
  }
}

export function credencialGuardada(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DA_CREDENCIAL)
  } catch {
    // Navegador com armazenamento bloqueado: a sessao passa a durar so
    // enquanto a pagina estiver aberta. Melhor que derrubar a tela inteira.
    return null
  }
}

export function contaGuardada(): Conta | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE_DA_CONTA)
    if (bruto === null) return null
    const conta = JSON.parse(bruto) as Conta
    // QUEM JA ESTAVA DENTRO NAO E POSTO PARA FORA. A entrada so passou a
    // devolver o e-mail em 09/09/2026, e a conta guardada antes disso nao o
    // tem. Derrubar a sessao por causa de um campo novo seria trocar um
    // defeito por outro; o vazio e honesto, e quem precisa dele o busca no
    // perfil (ver `Aplicacao`). A mesma regra vale para `perfis` e
    // `entidades` (spec 0072): a troca de entidade so aparece na proxima
    // entrada.
    return {
      ...conta,
      email: typeof conta.email === "string" ? conta.email : "",
      cpf: typeof conta.cpf === "string" ? conta.cpf : "",
      perfis: Array.isArray(conta.perfis) ? conta.perfis : [conta.papel],
      perfis_na_tela: Array.isArray(conta.perfis_na_tela) ? conta.perfis_na_tela : [],
      entidades: Array.isArray(conta.entidades) ? conta.entidades : conta.entidade ? [conta.entidade] : [],
    }
  } catch {
    return null
  }
}

export function guardarSessao(credencial: string, conta: Conta): void {
  try {
    sessionStorage.setItem(CHAVE_DA_CREDENCIAL, credencial)
    sessionStorage.setItem(CHAVE_DA_CONTA, JSON.stringify(conta))
  } catch {
    /* sem armazenamento, a sessao vive so na memoria desta pagina */
  }
}

/**
 * Troca SÓ a credencial, mantendo a conta guardada.
 *
 * Serve a um caso só, e ele é o da troca de senha: o servidor derruba toda
 * credencial emitida antes da troca — inclusive a de quem acabou de trocar —, e
 * devolve uma nova no lugar. Sem esta função, quem trocasse a senha seria
 * mandado à tela de entrada no instante seguinte a ter provado a identidade
 * duas vezes.
 */
export function guardarCredencial(credencial: string): void {
  try {
    sessionStorage.setItem(CHAVE_DA_CREDENCIAL, credencial)
  } catch {
    /* sem armazenamento, a credencial vive so na memoria desta pagina */
  }
}

export function atualizarContaGuardada(conta: Conta): void {
  try {
    sessionStorage.setItem(CHAVE_DA_CONTA, JSON.stringify(conta))
  } catch {
    /* idem */
  }
}

/** A resposta de `POST /sessao` e de `POST /sessao/entidade`. */
export type RespostaDaEntrada = { credencial: string } & Conta

/** A conta que a tela guarda, a partir da resposta da entrada ou da troca. */
export function contaDaEntrada(resposta: RespostaDaEntrada): Conta {
  return {
    usuario_id: resposta.usuario_id,
    nome: resposta.nome,
    email: resposta.email,
    cpf: resposta.cpf ?? "",
    papel: resposta.papel,
    perfis: resposta.perfis ?? [resposta.papel],
    perfis_na_tela: resposta.perfis_na_tela ?? [],
    is_root: resposta.is_root,
    is_demo: resposta.is_demo,
    entidade: resposta.entidade,
    entidades: resposta.entidades ?? [],
    senha_provisoria: resposta.senha_provisoria,
    senha_alterada_em: resposta.senha_alterada_em,
    troca_de_senha_exigida: resposta.troca_de_senha_exigida,
    troca_de_senha_bloqueia: resposta.troca_de_senha_bloqueia,
    politica_da_senha: resposta.politica_da_senha,
  }
}

/**
 * A ÚLTIMA ENTIDADE EM QUE A PESSOA TRABALHOU (spec 0072, KD-2).
 *
 * Só o id, no `localStorage`, por conta: nenhum segredo, e quem trabalha sempre
 * na mesma entidade confirma a escolha com um clique. Sem armazenamento, ou com
 * um id que já não está entre as entidades da pessoa, vale a primeira da lista.
 */
export function ultimaEntidade(usuarioId: number): number | null {
  try {
    const bruto = localStorage.getItem(`${PREFIXO_DA_ULTIMA_ENTIDADE}${usuarioId}`)
    const id = bruto === null ? NaN : Number(bruto)
    return Number.isInteger(id) ? id : null
  } catch {
    return null
  }
}

export function lembrarEntidade(usuarioId: number, entidadeId: number): void {
  try {
    localStorage.setItem(`${PREFIXO_DA_ULTIMA_ENTIDADE}${usuarioId}`, String(entidadeId))
  } catch {
    /* sem armazenamento, a próxima entrada marca a primeira da lista */
  }
}

export function esquecerSessao(): void {
  try {
    sessionStorage.removeItem(CHAVE_DA_CREDENCIAL)
    sessionStorage.removeItem(CHAVE_DA_CONTA)
  } catch {
    /* nada a limpar */
  }
}

async function chamar<T>(caminho: string, opcoes: RequestInit = {}): Promise<T> {
  const credencial = credencialGuardada()
  const cabecalhos: Record<string, string> = {
    "Content-Type": "application/json",
    ...((opcoes.headers as Record<string, string>) ?? {}),
  }
  // Quem chama pode mandar a credencial (a troca de entidade logo depois da
  // senha, antes de a sessao ser guardada); senao, vale a guardada.
  if (credencial !== null && cabecalhos["Authorization"] === undefined) {
    cabecalhos["Authorization"] = `Bearer ${credencial}`
  }

  const resposta = await fetch(`/api${caminho}`, { ...opcoes, headers: cabecalhos })

  if (resposta.status === 204) return undefined as T

  let corpo: unknown = null
  try {
    corpo = await resposta.json()
  } catch {
    corpo = null
  }

  if (!resposta.ok) {
    // SESSAO QUE DEIXOU DE VALER SO TEM UM CAMINHO: entrar de novo.
    //
    // O servidor passou a conferir a credencial contra o banco — conta
    // existe, esta ativa, e a entidade e a mesma. Quando uma dessas falha,
    // insistir na tela nao adianta: nenhuma outra chamada vai passar.
    //
    // O aviso sai daqui, e nao de cada tela, porque toda chamada passa por
    // esta funcao e nenhuma tela deveria precisar saber disso.
    //
    // Sem credencial guardada não havia sessão para cair: é a senha errada na
    // entrada, e a própria tela diz "CPF ou senha não conferem" (08/10/2026).
    if (resposta.status === 401 && credencialGuardada() !== null) {
      esquecerSessao()
      window.dispatchEvent(new CustomEvent("sessao-expirada"))
    }
    throw new ErroDaApi(
      resposta.status,
      (corpo as Record<string, unknown>) ?? {},
    )
  }
  return corpo as T
}

/**
 * A IMAGEM PROTEGIDA — brasão e foto — como `Blob`, com a credencial.
 *
 * `<img src="/api/...">` não manda o cabeçalho `Authorization`, e a imagem de
 * uma entidade não é pública. Busca-se com a credencial e a tela monta um
 * endereço local do objeto. Ausência (404) volta `null`: entidade sem brasão é
 * o caso comum, e não falha.
 */
async function buscarImagem(caminho: string): Promise<Blob | null> {
  const credencial = credencialGuardada()
  const resposta = await fetch(`/api${caminho}`, {
    headers: credencial === null ? {} : { Authorization: `Bearer ${credencial}` },
  })
  if (resposta.status === 404) return null
  if (!resposta.ok) {
    let corpo: Record<string, unknown> = {}
    try {
      corpo = (await resposta.json()) as Record<string, unknown>
    } catch {
      corpo = {}
    }
    throw new ErroDaApi(resposta.status, corpo)
  }
  return resposta.blob()
}

export const api = {
  /** Entra pelo CPF (spec 0073); `cpf` só com algarismos. */
  entrar: (cpf: string, senha: string) =>
    chamar<RespostaDaEntrada>("/sessao", {
      method: "POST",
      body: JSON.stringify({ cpf, senha }),
    }),
  /** Troca a entidade da sessão (spec 0072). `credencial` é para a troca logo
   *  depois da senha, quando a sessão ainda não foi guardada. */
  // `confirmaAEntrada`: o passo da escolha, logo depois da senha; a entrada se
  // registra na entidade confirmada (spec 0086). Sem ele, é a troca pelo topo.
  trocarEntidade: (entidadeId: number, credencial?: string, opcoes: { confirmaAEntrada?: boolean } = {}) =>
    chamar<RespostaDaEntrada>("/sessao/entidade", {
      method: "POST",
      body: JSON.stringify(
        opcoes.confirmaAEntrada ? { entidade_id: entidadeId, confirma_a_entrada: true } : { entidade_id: entidadeId },
      ),
      headers: credencial === undefined ? {} : { Authorization: `Bearer ${credencial}` },
    }),
  // Seguir sem trocar a senha provisória fica na trilha (spec 0086).
  adiarTrocaDeSenha: () => chamar<void>("/sessao/troca-adiada", { method: "POST" }),
  contratos: (consulta: string) => chamar<RespostaDeLista>(`/contratos${consulta}`),
  ficha: (id: number) => chamar<Ficha>(`/contratos/${id}`),
  indicadores: () => chamar<Indicadores>("/indicadores"),
  // A FILA E A LINHA DO TEMPO DO PAINEL (F43). A lista de alertas (`/alertas`)
  // continua no servidor para os avisos; a tela passou a pedir a fila, que
  // agrupa por contrato — agrupar aqui seria a segunda conta.
  painelFila: (consulta: {
    filtro: FiltroDaFila
    faixa: string | null
    pagina: number
    porPagina?: number
    ordem?: OrdemDaFila | null
    gravidade?: Gravidade | null
  }) => {
    const parametros = new URLSearchParams({
      filtro: consulta.filtro,
      pagina: String(consulta.pagina),
      por_pagina: String(consulta.porPagina ?? 10),
    })
    if (consulta.faixa !== null) parametros.set("faixa", consulta.faixa)
    if (consulta.ordem !== undefined && consulta.ordem !== null) parametros.set("ordem", consulta.ordem)
    if (consulta.gravidade !== undefined && consulta.gravidade !== null) parametros.set("gravidade", consulta.gravidade)
    return chamar<FilaDoPainel>(`/painel/fila?${parametros.toString()}`)
  },
  painelVencimentos: () => chamar<LinhaDoTempo>("/painel/vencimentos"),

  // --- a AREA DO FISCAL, e as unicas chamadas que ESCREVEM ----------------
  fiscalizacao: () =>
    chamar<{ dados: ContratoSobFiscalizacao[]; meta: Record<string, number> }>(
      "/fiscalizacao",
    ),
  fiscalizacaoDoContrato: (id: number) =>
    chamar<DetalheDaFiscalizacao>(`/fiscalizacao/${id}`),
  // Os atos sem volta levam a senha de quem os faz; o servidor a confere (spec 0082).
  atestar: (medicaoId: number, aceita: boolean, ressalva: string | null, senha: string) =>
    chamar<MedicaoNaTela>(`/fiscalizacao/medicoes/${medicaoId}/ateste`, {
      method: "POST",
      body: JSON.stringify({ aceita, ressalva, senha }),
    }),
  agentes: () => chamar<LeituraDosAgentes>("/agentes"),
  athus: () => chamar<EspelhoDoAthus>("/integracao/athus"),
  categoriasDoAthus: () => chamar<CategoriasDoAthus>("/integracao/athus/categorias"),
  testarAthus: () => chamar<TesteDaConexaoComOAthus>("/integracao/athus/testar"),
  historicoDaImportacao: () => chamar<ExecucaoDaImportacao[]>("/integracao/athus/importacao/historico"),
  importacaoAutomatica: () => chamar<ImportacaoAutomatica>("/integracao/athus/importacao"),
  mudarImportacaoAutomatica: (corpo: { ligada: boolean; entidade_id: number | null; limite: number }) =>
    chamar<ImportacaoAutomatica>("/integracao/athus/importacao", {
      method: "POST",
      body: JSON.stringify(corpo),
    }),
  importarAgora: () =>
    chamar<ImportacaoAutomatica>("/integracao/athus/importacao/agora", { method: "POST" }),
  pecas: (contratoId: number) =>
    chamar<PecasDoContrato>(`/contratos/${contratoId}/pecas`),
  processo: (contratoId: number, recorte = "tudo", busca = "") =>
    chamar<ProcessoDoContrato>(
      `/contratos/${contratoId}/processo?recorte=${encodeURIComponent(recorte)}` +
        `&busca=${encodeURIComponent(busca)}`,
    ),
  avaliarPeca: (
    contratoId: number,
    pecaId: number,
    decisao: string,
    texto: string,
    senha: string,
  ) =>
    chamar<RespostaDaAvaliacao>(
      `/contratos/${contratoId}/pecas/${pecaId}/avaliacoes`,
      { method: "POST", body: JSON.stringify({ decisao, texto, senha }) },
    ),
  avaliacoesDaPeca: (contratoId: number, pecaId: number) =>
    chamar<AvaliacaoNaTela[]>(`/contratos/${contratoId}/pecas/${pecaId}/avaliacoes`),
  autenticidade: (codigo: string) =>
    chamar<ConferenciaDeAutenticidade>(
      `/autenticidade/${encodeURIComponent(codigo)}`,
    ),
  // Com `pagina`, a caixa vem paginada pelo servidor (spec 0083); sem, os
  // `limite` mais recentes, como o sino pede.
  // `filtros` (26/10/2026): gravidade e ordenação no servidor, porque a caixa
  // pagina lá e ordenar na tela ordenaria só a página aberta.
  avisos: (
    apenasNaoLidos = false,
    limite = 100,
    pagina?: { pagina: number; porPagina: number },
    filtros?: { gravidade?: string; ordenarPor?: "data" | "gravidade" | "contrato"; ordem?: "asc" | "desc" },
  ) =>
    chamar<{
      dados: AvisoNaTela[]
      meta: { nao_lidos: number; total: number; pagina: number; por_pagina: number }
    }>(
      `/avisos?apenas_nao_lidos=${apenasNaoLidos}&limite=${limite}` +
        (pagina === undefined ? "" : `&pagina=${pagina.pagina}&por_pagina=${pagina.porPagina}`) +
        (filtros?.gravidade ? `&gravidade=${filtros.gravidade}` : "") +
        (filtros?.ordenarPor ? `&ordenar_por=${filtros.ordenarPor}&ordem=${filtros.ordem ?? "desc"}` : ""),
    ),
  marcarAvisoLido: (id: number) =>
    chamar<{ dados: AvisoNaTela[]; meta: { nao_lidos: number; total: number } }>(`/avisos/${id}/lida`, {
      method: "POST",
    }),
  marcarTodosAvisosLidos: () =>
    chamar<{ dados: AvisoNaTela[]; meta: { nao_lidos: number; total: number } }>("/avisos/lidas", {
      method: "POST",
    }),
  minhaConta: () => chamar<MinhaConta>("/minha-conta"),
  mudarMinhaConta: (mudanca: { telefone?: string; avisar_por_email?: boolean; avisar_por_whatsapp?: boolean }) =>
    chamar<MinhaConta>("/minha-conta", { method: "POST", body: JSON.stringify(mudanca) }),
  situacaoDosAvisos: () => chamar<SituacaoDosAvisos>("/configuracoes/avisos"),
  processarAvisos: () =>
    chamar<ResultadoDoProcessamento>("/configuracoes/avisos/processar", { method: "POST" }),
  reiniciarAvisos: () =>
    chamar<{ avisos_apagados: number }>("/configuracoes/avisos/reiniciar", { method: "POST" }),
  testarCanal: (canal: string) =>
    chamar<ResultadoDoTeste>("/configuracoes/avisos/testar", {
      method: "POST",
      body: JSON.stringify({ canal }),
    }),
  painelExecutivo: () => chamar<PainelExecutivo>("/painel/executivo"),
  painelOperacional: () => chamar<PainelOperacional>("/painel/operacional"),
  relatorios: () => chamar<RelatorioDisponivel[]>("/relatorios"),
  vigencia: (contratoId: number) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/vigencia`),
  datasDoContrato: (contratoId: number) =>
    chamar<DatasDoContrato>(`/contratos/${contratoId}/datas`),
  /* A data que vale (F44): so inicio e termino da vigencia. Declarar de novo revisa. */
  declararData: (
    contratoId: number,
    chave: string,
    declaracao: {
      data: string
      motivo: string | null
      peca_id: number | null
    },
  ) =>
    chamar<DatasDoContrato>(`/contratos/${contratoId}/datas/${chave}/declaracao`, {
      method: "POST",
      body: JSON.stringify(declaracao),
    }),
  retirarData: (contratoId: number, chave: string, motivo: string | null) =>
    chamar<DatasDoContrato>(`/contratos/${contratoId}/datas/${chave}/declaracao/retirar`, {
      method: "POST",
      body: JSON.stringify({ motivo }),
    }),
  recalcularVigencia: (contratoId: number) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/vigencia/recalcular`, { method: "POST" }),
  validarVigencia: (contratoId: number, motivo: string, memoriaId: number | null, senha: string) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/vigencia/validar`, {
      method: "POST",
      body: JSON.stringify({ motivo, memoria_id: memoriaId, senha }),
    }),
  contestarVigencia: (contratoId: number, motivo: string, memoriaId: number | null, senha: string) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/vigencia/contestar`, {
      method: "POST",
      body: JSON.stringify({ motivo, memoria_id: memoriaId, senha }),
    }),
  registrarEvento: (
    contratoId: number,
    evento: {
      tipo: string
      data: string
      fonte: string
      evidencia?: string | null
      peca_id?: number | null
      /* As tres provas da publicacao: a data ja vai acima; estas duas a tornam conferivel. */
      veiculo?: string | null
      referencia?: string | null
      pagina?: number | null
    },
    senha: string,
  ) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/eventos`, {
      method: "POST",
      body: JSON.stringify({ ...evento, senha }),
    }),
  mudarProvidencia: (contratoId: number, providenciaId: number, situacao: string, observacao: string | null) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/providencias/${providenciaId}`, {
      method: "POST",
      body: JSON.stringify({ situacao, observacao }),
    }),
  definirCriticidade: (contratoId: number, criticidade: string | null) =>
    chamar<VigenciaDoContrato>(`/contratos/${contratoId}/criticidade`, {
      method: "POST",
      body: JSON.stringify({ criticidade }),
    }),
  contas: () => chamar<RespostaDasContas>("/configuracoes/contas"),
  criarConta: (pedido: {
    nome: string
    email: string
    /** Obrigatório (spec 0073), só com algarismos. */
    cpf: string
    senha: string
    perfis: string[]
    setor_id?: number
    /** Só o ROOT escolhe a entidade (spec 0072); o gestor cria na da sessão. */
    entidade_id?: number
  }) =>
    chamar<ContaNaTela>("/configuracoes/contas", {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  mudarConta: (contaId: number, corpo: { perfis?: string[]; ativo?: boolean }) =>
    chamar<ContaNaTela>(`/configuracoes/contas/${contaId}`, {
      method: "POST",
      body: JSON.stringify(corpo),
    }),
  redefinirSenha: (contaId: number, senhaNova: string, senha: string) =>
    chamar<ContaNaTela>(`/configuracoes/contas/${contaId}/redefinir-senha`, {
      method: "POST",
      body: JSON.stringify({ senha_nova: senhaNova, senha }),
    }),
  auditoria: (filtro: FiltroDaTrilha = {}) => {
    const consulta = new URLSearchParams()
    if (filtro.pagina !== undefined) consulta.set("pagina", String(filtro.pagina))
    if (filtro.por_pagina !== undefined)
      consulta.set("por_pagina", String(filtro.por_pagina))
    if (filtro.autor_id !== undefined && filtro.autor_id !== null)
      consulta.set("autor_id", String(filtro.autor_id))
    if (filtro.tabela) consulta.set("tabela", filtro.tabela)
    if (filtro.de) consulta.set("de", filtro.de)
    if (filtro.ate) consulta.set("ate", filtro.ate)
    const texto = consulta.toString()
    return chamar<RespostaDaTrilha>(
      `/configuracoes/auditoria${texto === "" ? "" : `?${texto}`}`,
    )
  },
  perfil: (atos?: number) => chamar<Perfil>(atos === undefined ? "/perfil" : `/perfil?atos=${atos}`),

  // --- F41: o perfil completo ---------------------------------------------
  alterarPerfil: (mudanca: Record<string, string | number | null>) =>
    chamar<Perfil>("/perfil", { method: "PATCH", body: JSON.stringify(mudanca) }),
  enviarMinhaFoto: (arquivo: Blob) =>
    chamar<RespostaDaImagem>("/perfil/foto", {
      method: "PUT",
      body: arquivo,
      headers: { "Content-Type": arquivo.type || "application/octet-stream" },
    }),
  minhaFoto: () => buscarImagem("/perfil/foto"),
  minhasEntradas: (limite = 15) =>
    chamar<RespostaDasEntradas>(`/perfil/entradas?limite=${limite}`),
  /** Vincula à entidade quem já tem conta (spec 0070). */
  vincularConta: (pedido: { email?: string; cpf?: string; setor_id?: number; entidade_id?: number }) =>
    chamar<ContaNaTela>("/configuracoes/contas/vincular", { method: "POST", body: JSON.stringify(pedido) }),
  /** Os setores da entidade da sessão; o ROOT informa a entidade. */
  setores: (entidadeId?: number, incluir?: number | null) => {
    const busca = new URLSearchParams()
    if (entidadeId !== undefined) busca.set("entidade_id", String(entidadeId))
    if (incluir !== undefined && incluir !== null) busca.set("incluir", String(incluir))
    const texto = busca.toString()
    return chamar<SetorNaTela[]>(`/configuracoes/setores${texto === "" ? "" : `?${texto}`}`)
  },
  cadastroDeSetores: (entidadeId?: number) =>
    chamar<RespostaDosSetores>(`/configuracoes/setores/cadastro${entidadeId === undefined ? "" : `?entidade_id=${entidadeId}`}`),
  criarSetor: (pedido: PedidoDeSetor) =>
    chamar<RespostaDosSetores>("/configuracoes/setores", { method: "POST", body: JSON.stringify(pedido) }),
  alterarSetor: (id: number, mudanca: MudancaDeSetor) =>
    chamar<RespostaDosSetores>(`/configuracoes/setores/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  cargos: (incluir?: number | null) =>
    chamar<CargoNaTela[]>(`/catalogos/cargos${incluir === undefined || incluir === null ? "" : `?incluir=${incluir}`}`),
  cadastroDeTiposDeEntidade: () => chamar<RespostaDosTiposDeEntidade>("/configuracoes/tipos-de-entidade"),
  criarTipoDeEntidade: (nome: string) =>
    chamar<RespostaDosTiposDeEntidade>("/configuracoes/tipos-de-entidade", { method: "POST", body: JSON.stringify({ nome }) }),
  alterarTipoDeEntidade: (id: number, mudanca: { nome?: string; ativo?: boolean }) =>
    chamar<RespostaDosTiposDeEntidade>(`/configuracoes/tipos-de-entidade/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  cadastroDeCargos: () => chamar<RespostaDosCargos>("/configuracoes/cargos"),
  criarCargo: (nome: string) =>
    chamar<RespostaDosCargos>("/configuracoes/cargos", { method: "POST", body: JSON.stringify({ nome }) }),
  alterarCargo: (id: number, mudanca: { nome?: string; ativo?: boolean }) =>
    chamar<RespostaDosCargos>(`/configuracoes/cargos/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  juntarCargos: (id: number, destinoId: number) =>
    chamar<RespostaDosCargos>(`/configuracoes/cargos/${id}/juntar`, {
      method: "POST",
      body: JSON.stringify({ destino_id: destinoId }),
    }),
  dadosDaConta: (contaId: number) =>
    chamar<DadosFuncionaisDaConta>(`/configuracoes/contas/${contaId}`),
  alterarDadosDaConta: (contaId: number, mudanca: Record<string, string | number | null>) =>
    chamar<DadosFuncionaisDaConta>(`/configuracoes/contas/${contaId}`, {
      method: "PATCH",
      body: JSON.stringify(mudanca),
    }),

  // --- F41: as entidades e o licenciamento --------------------------------
  entidades: (busca = "") =>
    chamar<RespostaDasEntidades>(`/entidades?busca=${encodeURIComponent(busca)}`),
  entidade: (id: number) => chamar<FichaDaEntidade>(`/entidades/${id}`),
  mudarSituacaoDaEntidade: (id: number, ativa: boolean) =>
    chamar<FichaDaEntidade>(`/entidades/${id}/situacao`, { method: "PATCH", body: JSON.stringify({ ativa }) }),
  criarEntidade: (pedido: MudancaDoCadastro) =>
    chamar<FichaDaEntidade>("/entidades", { method: "POST", body: JSON.stringify(pedido) }),
  alterarEntidade: (id: number, mudanca: MudancaDoCadastro) =>
    chamar<FichaDaEntidade>(`/entidades/${id}`, {
      method: "PATCH",
      body: JSON.stringify(mudanca),
    }),
  enviarBrasao: (id: number, arquivo: Blob) =>
    chamar<RespostaDaImagem>(`/entidades/${id}/brasao`, {
      method: "PUT",
      body: arquivo,
      headers: { "Content-Type": arquivo.type || "application/octet-stream" },
    }),
  brasao: (id: number) => buscarImagem(`/entidades/${id}/brasao`),
  definirSistemas: (id: number, sistemas: SistemaNoPedido[]) =>
    chamar<SistemaDaEntidadeNaTela[]>(`/entidades/${id}/sistemas`, {
      method: "PUT",
      body: JSON.stringify({ sistemas }),
    }),
  catalogoDeSistemas: () => chamar<SistemaDoCatalogo[]>("/catalogos/sistemas-datafull"),

  // --- F42: Configurações em áreas ----------------------------------------
  alcanceDeConfiguracoes: () => chamar<AlcanceDeConfiguracoes>("/configuracoes/alcance"),
  regrasDoSistema: (busca = "") =>
    chamar<RespostaDasRegras>(`/configuracoes/parametros?busca=${encodeURIComponent(busca)}`),
  novaVigencia: (chave: string, pedido: PedidoDeVigencia, senha: string) =>
    chamar<VigenciaNaTela>(`/configuracoes/parametros/${encodeURIComponent(chave)}/vigencias`, {
      method: "POST",
      body: JSON.stringify({ ...pedido, senha }),
    }),
  faixasDeProvidencia: () => chamar<RespostaDasFaixas>("/configuracoes/faixas"),
  criarFaixa: (pedido: PedidoDeFaixa) =>
    chamar<FaixaNaTela>("/configuracoes/faixas", { method: "POST", body: JSON.stringify(pedido) }),
  alterarFaixa: (id: number, mudanca: Partial<Pick<FaixaNaTela, "providencia" | "responsavel" | "escalonamento" | "ativo">>) =>
    chamar<FaixaNaTela>(`/configuracoes/faixas/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  tiposDePeca: (busca = "") =>
    chamar<RespostaDosTipos>(`/configuracoes/tipos-de-peca?busca=${encodeURIComponent(busca)}`),
  alterarTipoDePeca: (id: number, mudanca: { papel?: string; ativo?: boolean; observacao?: string | null }) =>
    chamar<TipoNaTela>(`/configuracoes/tipos-de-peca/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  tiposDeSetor: () => chamar<RespostaDosTiposDeSetor>("/configuracoes/tipos-de-setor"),
  criarTipoDeSetor: (tipo: string) =>
    chamar<RespostaDosTiposDeSetor>("/configuracoes/tipos-de-setor", { method: "POST", body: JSON.stringify({ tipo }) }),
  alterarTipoDeSetor: (id: number, mudanca: { tipo?: string; ativo?: boolean }) =>
    chamar<RespostaDosTiposDeSetor>(`/configuracoes/tipos-de-setor/${id}`, { method: "PATCH", body: JSON.stringify(mudanca) }),
  catalogoCompleto: () => chamar<RespostaDoCatalogo>("/configuracoes/sistemas-datafull"),
  acrescentarSistema: (pedido: { sigla: string; nome: string; descricao: string | null }) =>  // sem-tela-ok: o botão de acrescentar saiu da tela a pedido do usuário (20/10/2026, spec 0111); a rota continua
    chamar<RespostaDoCatalogo>("/configuracoes/sistemas-datafull", {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  alterarSistema: (id: number, mudanca: { nome?: string; descricao?: string | null; ativo?: boolean }) =>
    chamar<RespostaDoCatalogo>(`/configuracoes/sistemas-datafull/${id}`, {
      method: "PATCH",
      body: JSON.stringify(mudanca),
    }),

  trocarMinhaSenha: (senhaAtual: string, senhaNova: string) =>
    chamar<{ credencial: string; perfil: Perfil }>("/minha-conta/senha", {
      method: "POST",
      body: JSON.stringify({ senha_atual: senhaAtual, senha_nova: senhaNova }),
    }),
  regularidade: (contratoId: number, em?: string) =>
    chamar<RegularidadeDoContrato>(
      `/fiscalizacao/${contratoId}/regularidade${em ? `?em=${em}` : ""}`,
    ),
  validarRegularidade: (contratoId: number) =>
    chamar<ValidacaoNaTela[]>(
      `/fiscalizacao/${contratoId}/regularidade/validar`,
      { method: "POST" },
    ),
  registrarCertidao: (
    contratoId: number,
    pedido: {
      tipo: string
      validade: string
      numero: string | null
      emissao: string | null
      observacao: string | null
    },
    senha: string,
  ) =>
    chamar<LinhaDeRegularidade>(`/fiscalizacao/${contratoId}/regularidade`, {
      method: "POST",
      body: JSON.stringify({ ...pedido, senha }),
    }),
  conferencia: (contratoId: number) =>
    chamar<ConferenciaDoMes>(`/fiscalizacao/${contratoId}/conferencia`),
  marcarConferencia: (
    itemId: number,
    situacao: string,
    observacao: string | null,
  ) =>
    chamar<ItemDeConferenciaNaTela>(`/fiscalizacao/conferencia/${itemId}`, {
      method: "POST",
      body: JSON.stringify({ situacao, observacao }),
    }),
  registrarOcorrencia: (
    contratoId: number,
    pedido: {
      titulo: string
      descricao: string
      gravidade: string
      providencia: string | null
    },
    senha: string,
  ) =>
    chamar<OcorrenciaNaTela>(`/fiscalizacao/${contratoId}/ocorrencias`, {
      method: "POST",
      body: JSON.stringify({ ...pedido, senha }),
    }),

  /* --- os blocos da ficha que se editam (F35) --------------------------
   * Todas devolvem a FICHA INTEIRA depois de gravar. Devolver só o campo
   * alterado obrigaria a tela a remontar o estado à mão, e foi assim que um
   * bloco salvo passou a mostrar o valor novo ao lado de um total antigo. */
  editarIdentificacao: (
    contratoId: number,
    pedido: {
      numero: string
      ano: number
      processo_administrativo: string | null
      tipo: string
      modalidade: string
      natureza_do_objeto: string | null
      prorrogavel: boolean | null
      prorrogacao_limite: string | null
    },
  ) =>
    chamar<Ficha>(`/contratos/${contratoId}/identificacao`, {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  editarObjeto: (
    contratoId: number,
    pedido: {
      objeto: string
      valor_inicial: string | null
      valor_atual: string | null
    },
  ) =>
    chamar<Ficha>(`/contratos/${contratoId}/objeto`, {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  contratados: (busca: string) =>
    chamar<ContratadoNaBusca[]>(
      `/contratados?busca=${encodeURIComponent(busca)}`,
    ),
  escolherContratado: (
    contratoId: number,
    pedido: {
      contratado_id?: number
      documento?: string
      razao_social?: string
    },
  ) =>
    chamar<Ficha>(`/contratos/${contratoId}/contratado`, {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  pessoas: () => chamar<PessoaDaEntidade[]>("/pessoas"),
  designar: (
    contratoId: number,
    pedido: {
      usuario_id: number
      papel: string
      ato_de_designacao: string | null
      inicio: string | null
    },
  ) =>
    chamar<Ficha>(`/contratos/${contratoId}/designacoes`, {
      method: "POST",
      body: JSON.stringify(pedido),
    }),
  encerrarDesignacao: (
    contratoId: number,
    designacaoId: number,
    motivo: string | null,
    senha: string,
  ) =>
    chamar<Ficha>(
      `/contratos/${contratoId}/designacoes/${designacaoId}/encerrar`,
      { method: "POST", body: JSON.stringify({ motivo, senha }) },
    ),

  /* --- o quadro do agente, que saiu da ficha (F35) ---------------------- */
  contratosLidosPelosAgentes: (busca: string) =>
    chamar<ContratoComLeitura[]>(
      `/agentes/contratos?busca=${encodeURIComponent(busca)}`,
    ),
  leituraDoContrato: (contratoId: number) =>
    chamar<LeituraDeUmContrato>(`/agentes/contratos/${contratoId}`),

  laudosDoContrato: (contratoId: number) =>
    chamar<LaudoNaTela[]>(`/contratos/${contratoId}/laudos`),
}

// --- a area do fiscal -------------------------------------------------------

export type ContratoSobFiscalizacao = {
  contrato_id: number
  numero: string
  ano: number
  objeto: string
  papel: string
  situacao: string
  vigencia_fim: string | null
  /** NULO = a vigencia nao foi lida. Nao e "vence hoje". */
  dias_para_vencer: number | null
  medicoes_aguardando: number
  valor_aguardando: string
  ocorrencias_abertas: number
  /** Itens da conferência do mês corrente ainda não resolvidos. */
  conferencia_pendente: number
}

export type MedicaoNaTela = {
  id: number
  numero: number
  competencia_inicio: string
  competencia_fim: string
  valor: string
  situacao: string
  /** Data E HORA do ateste: duas medicoes do mesmo dia tem ordem. */
  atestada_em: string | null
  ressalva: string | null
}

export type OcorrenciaNaTela = {
  id: number
  data: string
  gravidade: string
  titulo: string
  descricao: string
  providencia: string | null
}

export type DetalheDaFiscalizacao = {
  /** NULO = quem pediu nao esta designado para este contrato. */
  papel: string | null
  medicoes: MedicaoNaTela[]
  ocorrencias: OcorrenciaNaTela[]
}

// --- o que a API devolve ----------------------------------------------------

export type Meta = {
  total: number
  pagina: number
  por_pagina: number
  paginas: number
  avisos: string[]
}

export type ContratoNaLista = {
  id: number
  numero: string
  ano: number
  objeto: string
  tipo: string
  modalidade: string
  situacao: string
  vigencia_inicio: string | null
  vigencia_fim: string | null
  /** NULO = valor desconhecido (ainda dentro do PDF). Zero = valor medido. */
  valor_inicial: string | null
  valor_atual: string | null
  contratado: { id: number; documento: string; razao_social: string }
  is_demo: boolean
  /** Nulo = nasceu neste sistema. `athus` = espelhado do ATHUS. */
  origem: string | null
  origem_processo: string | null
  /** NULO = a cláusula não foi lida. Não é o mesmo que FALSO. */
  prorrogavel: boolean | null
  natureza_do_objeto: string | null
  extraido_em: string | null
}

export type RespostaDeLista = { dados: ContratoNaLista[]; meta: Meta }

export type Indicadores = {
  contratos_vigentes: number | null
  valor_vigente: string | null
  vencendo_em_30: number | null
  vencendo_em_60: number | null
  vencendo_em_90: number | null
  medicoes_sem_ateste: number | null
  /** Quantos CONTRATOS estão por trás das medições sem ateste. São duas
   *  medidas, e o cartão mostra as duas: o clique abre a lista de contratos, e
   *  a contagem dela precisa estar escrita antes do clique. */
  contratos_com_medicao_sem_ateste: number | null
  valor_empenhado: string | null
  /** Zero explica o travessão do valor: nenhum empenho espelhado ainda. */
  empenhos_registrados: number | null
  valor_pago: string | null
  pagamentos_registrados: number | null
  contratos_sem_fiscal: number | null
  por_situacao: Record<string, number>
  por_modalidade: Record<string, number>
}

/** Os filtros da fila de trabalho do painel (F43). */
export type FiltroDaFila =
  | "todos"
  | "vencidos"
  | "vencem_90"
  | "sem_fiscal"
  | "providencias_atrasadas"
  | "sem_data_segura"

export const FILTROS_DA_FILA: readonly FiltroDaFila[] = [
  "todos",
  "vencidos",
  "vencem_90",
  "sem_fiscal",
  "providencias_atrasadas",
  "sem_data_segura",
]

export type OrdemDaFila = "contrato" | "-contrato"

export type Gravidade = "critico" | "atencao" | "informacao"

/** Uma linha da fila: UM contrato, com as marcas de todos os problemas dele.
 *  Substitui a lista de alertas por tipo, que repetia o mesmo contrato uma vez
 *  por providência atrasada e chamava alerta de contrato. */
export type LinhaDaFila = {
  contrato_id: number
  numero: string
  objeto: string | null
  contratado: string | null
  situacao: string
  gravidade: Gravidade
  dias: number | null
  fim: string | null
  data_provavel: boolean
  /** O prazo mais urgente, já em palavras: "vencido há 389 dias". */
  prazo: string
  /** A aba da ficha em que o problema mais urgente se resolve. */
  aba: string
  marcas: { chave: string; rotulo: string; gravidade: Gravidade }[]
  providencias_atrasadas: number
  providencias: {
    faixa_dias: number | null
    descricao: string | null
    atraso_dias: number | null
    responsavel: string | null
  }[]
  problemas: {
    tipo: string
    gravidade: Gravidade
    titulo: string
    o_que_fazer: string
    fundamento: string | null
    procedencia: string | null
  }[]
}

export type FilaDoPainel = {
  dados: LinhaDaFila[]
  filtro: FiltroDaFila
  faixa: string | null
  ordem: OrdemDaFila | null
  /** Contratos com pendência, sem o recorte da faixa (spec 0121). */
  precisam_de_acao: number
  /** Contagem de CONTRATOS distintos por filtro — nunca de alertas. */
  filtros: { chave: FiltroDaFila; rotulo: string; contratos: number }[]
  por_gravidade: Record<Gravidade, number>
  /** A gravidade escolhida (spec 0121), ou nula; combina com a aba. */
  gravidade: Gravidade | null
  /** Contratos de cada gravidade dentro da aba escolhida. */
  gravidades: { chave: Gravidade; rotulo: string; contratos: number }[]
  so_designados: boolean
  meta: { pagina: number; por_pagina: number; total: number; avisos: string[] }
}

export type LinhaDoTempo = {
  faixas: {
    chave: string
    rotulo: string
    tom: Gravidade
    contratos: number
    valor: string | null
    sem_valor: number
  }[]
  com_data: number
  sem_data: number
  ativos: number
  niveis: { nivel: string; rotulo: string; contratos: number }[]
  sem_definicao_segura: number
  sem_providencia_de_continuidade: number
}

export type ContratadoLido = {
  documento: string
  razao_social: string | null
  pagina: number | null
  trecho: string | null
}

export type Ficha = {
  id: number
  numero: string
  ano: number
  processo_administrativo: string | null
  objeto: string
  tipo: string
  modalidade: string
  /** O grupo do ATHUS não diz a modalidade; quem cuida do contrato escolhe (spec 0099). */
  modalidade_a_confirmar?: boolean
  fundamento_legal: string | null
  regime_de_execucao: string | null
  situacao: string
  data_assinatura: string | null
  vigencia_inicio: string | null
  vigencia_fim: string | null
  valor_inicial: string | null
  valor_atual: string | null
  is_demo: boolean
  origem: string | null
  origem_processo: string | null
  origem_documento_id: string | null
  prorrogavel: boolean | null
  prorrogacao_limite: string | null
  natureza_do_objeto: string | null
  /** NULA = não avaliada, que é diferente de baixa. */
  criticidade: string | null
  extraido_em: string | null
  extracao: {
    achados?: Array<{
      campo: string
      valor: string
      evidencia: string
      confianca: string
      regra: string
    }>
    nao_achados?: string[]
    aplicados?: string[]
    registrados_sem_aplicar?: string[]
    motivos?: Record<string, string>
  } | null
  contratado: { id: number; documento: string; razao_social: string }
  /** O contratado lido no documento, ainda não ligado ao contrato (spec 0098). */
  /** Se quem pede é da gestão do contrato e pode editar a ficha (spec 0100). */
  pode_editar?: boolean
  contratado_lido?: ContratadoLido | null
  itens: Array<{
    id: number
    ordem: number
    descricao: string
    unidade: string
    quantidade: string
    valor_unitario: string
    valor_total: string
  }>
  aditivos: Array<{
    id: number
    numero: string
    ordem: number
    tipo: string
    unilateral: boolean
    data: string
    valor: string
    percentual: string | null
    percentual_acumulado: string | null
    limite_aplicado: string | null
    limite_fundamento: string | null
    excedeu_o_limite: boolean
    nova_vigencia_fim: string | null
    justificativa: string | null
  }>
  apostilamentos: Array<{
    id: number
    numero: string | null
    ordem: number
    tipo: string
    data: string
    indice: string | null
    percentual: string | null
    valor: string
    justificativa: string | null
  }>
  medicoes: Array<{
    id: number
    numero: number
    competencia_inicio: string
    competencia_fim: string
    valor: string
    situacao: string
    atestada_em: string | null
    ressalva: string | null
  }>
  pagamentos: Array<{
    id: number
    documento_fiscal: string | null
    valor_bruto: string
    valor_retencoes: string
    valor_liquido: string
    data_liquidacao: string | null
    data_pagamento: string | null
    situacao: string
  }>
  fiscais: DesignacaoNaFicha[]
  garantias: Array<{
    id: number
    modalidade: string
    percentual: string | null
    valor: string
    vigencia_inicio: string | null
    vigencia_fim: string | null
    numero_do_documento: string | null
    emissor: string | null
  }>
  empenhos: Array<{
    id: number
    numero: string
    exercicio: number
    data: string
    tipo: string
    valor: string
    valor_anulado: string
  }>
  dotacoes: Array<{
    id: number
    exercicio: number
    unidade_orcamentaria: string | null
    funcional_programatica: string | null
    natureza_da_despesa: string | null
    fonte_de_recursos: string | null
    valor: string
  }>
  ocorrencias: Array<{
    id: number
    data: string
    gravidade: string
    titulo: string
    descricao: string
    providencia: string | null
  }>
  procedencia: ProcedenciaDoCampo[]
  vigencia: VigenciaNoCabecalho
  /** O que pede ato, contado como o Painel e a Minha fiscalização contam: a contagem das abas. */
  atencao: AtencaoNaFicha
}

export type AtencaoNaFicha = {
  providencias_atrasadas: number
  conferencia_pendente: number
  certidoes_vencidas: number
}

/** A designação — não o usuário. Ela sobrevive ao desligamento da conta. */
export type DesignacaoNaFicha = {
  id: number
  usuario_id: number
  papel: string
  ato_de_designacao: string | null
  inicio: string
  fim: string | null
  usuario_nome: string | null
  usuario_email: string | null
  /** Sem fim, ou com fim no futuro. Encerrada NÃO some da ficha. */
  vigente: boolean
}

/**
 * De onde veio o campo — o que a ficha mostra sob o rótulo **fundamento**.
 *
 * `como_foi_lido` é a frase do documento em que o agente se baseou. Ela NÃO
 * traz a regra nem o nome da coluna: o quadro do agente saiu da ficha na F35 e
 * mora em "Agentes de fiscalização", que é a tela de quem audita o agente.
 */
export type ProcedenciaDoCampo = {
  campo: string
  rotulo: string
  origem: "athus" | "agente" | "proprio"
  fundamento: string
  /** O que a tela escreve ao lado do campo, curto. */
  resumo: string
  /** A explicação, mostrada só ao passar o mouse. */
  dica: string | null
  reescrito_pela_carga: boolean
  como_foi_lido: string | null
}

/** O dado CALCULADO do cabeçalho, medido no servidor sobre a memória. */
export type VigenciaNoCabecalho = {
  inicio: string | null
  referencia: string | null
  fonte_da_data: string | null
  nivel: string | null
  definicao_segura: boolean
  /** Negativo = já venceu. NULO = não há data, e nulo não é zero. */
  dias_para_vencer: number | null
}

export type PessoaDaEntidade = {
  id: number
  nome: string
  email: string
  /** Formatado (000.000.000-00); a busca do Designar fiscal acha por ele. */
  cpf: string | null
  papel: string
  ativo: boolean
}

export type ContratadoNaBusca = {
  id: number
  documento: string
  razao_social: string
  tipo_de_pessoa: string
}

// --- os agentes e a integracao ----------------------------------------------

/** Um contrato em que o agente leu alguma coisa do PDF. */
export type ContratoComLeitura = {
  contrato_id: number
  numero: string
  ano: number
  objeto: string
  achados: number
  aplicados: number
  extraido_em: string | null
}

/**
 * O quadro do agente, por contrato — o que SAIU da ficha na F35.
 *
 * `regra` é a expressão regular que achou o valor, e ela aparece AQUI de
 * propósito: quem abre esta tela está auditando o agente, e auditar o agente
 * sem ver a regra dele é olhar o resultado e acreditar. Na ficha do fiscal
 * fica só a evidência, sob o link "como foi lido".
 */
export type LeituraDeUmContrato = {
  contrato_id: number
  numero: string
  ano: number
  objeto: string
  extraido_em: string | null
  achados: Array<{
    campo: string
    rotulo: string
    valor: string | null
    confianca: string | null
    regra: string | null
    evidencia: string | null
    aplicado: boolean
    motivo: string | null
  }>
  nao_achados: string[]
}

export type CampoLidoNaTela = {
  campo: string
  rotulo: string
  /** Quantas vezes o agente ACHOU o campo no PDF. Nao e taxa de acerto. */
  achou: number
  /** Quantas viraram valor no contrato. Sempre <= `achou`, de proposito. */
  aplicou: number
  so_registrou: number
  alta: number
  media: number
  baixa: number
  motivo_mais_comum: string | null
}

export type LeituraDosAgentes = {
  contratos: number
  lidos: number
  sem_leitura: number
  campos: CampoLidoNaTela[]
  anomalias: Record<string, number>
}

export type EspelhoDoAthus = {
  ligado: boolean
  base_da_api: string | null
  espelhados: number
  proprios: number
  aditivos: number
  apostilamentos: number
  com_leitura_de_pdf: number
  /** As colunas de rastreamento que o sistema escolheu NAO trazer. */
  colunas_que_nunca_saem: string[]
}

export type GrupoDaCategoria = {
  codigo: number
  nome: string
}

export type CategoriaDoAthus = {
  nome: string
  configurada: boolean
  grupos: GrupoDaCategoria[]
}

export type ExecucaoDaImportacao = {
  iniciada_em: string
  terminada_em: string | null
  entidade_sigla: string | null
  fonte: string | null
  contratos_criados: number
  contratos_atualizados: number
  pdfs_lidos: number
  falhas_de_leitura: number
  contratados_aguardando: number
  falha: string | null
  /** "relogio" ou "tela" (spec 0102). */
  origem: string
  pedida_por_nome: string | null
  /** Começou e não terminou: a API ou o relógio caiu no meio. */
  interrompida: boolean
  /** Até 20 falhas de leitura, para a tela dizer quais (spec 0116). */
  falhas?: string[]
}

export type TesteDaConexaoComOAthus = { respondeu: boolean; mensagem: string; milissegundos: number | null }

export type ImportacaoAutomatica = {
  ligada: boolean
  entidade_id: number | null
  entidade_nome: string | null
  limite: number
  /** Só o Root real liga, desliga e escolhe (spec 0101). */
  pode_mudar: boolean
  entidades: { id: number; nome: string; sigla: string }[]
  /** O que falta para ligar: variáveis do ATHUS e a entidade. */
  falta: string[]
  ultima: ExecucaoDaImportacao | null
  em_andamento: boolean
  andamento_desde: string | null
  /** O Root, com o ATHUS configurado, a entidade escolhida e nenhuma em andamento. */
  pode_importar_agora: boolean
  /** Em que etapa a importação em andamento está, e quanto dela já foi (spec 0115). */
  andamento?: AndamentoDaImportacao | null
  /** Quando o relógio roda de novo; nula com a importação desligada (spec 0116). */
  proxima_rodada?: string | null
  /** A próxima rodada do relógio, ligada ou não, para a tela dizer a hora. */
  horario_da_rodada?: string | null
}

export type AndamentoDaImportacao = {
  etapa: "importando" | "lendo" | "processando"
  feitos: number | null
  total: number | null
}

export type CategoriasDoAthus = {
  /** Falso quando a API não está configurada ou não respondeu; o motivo diz qual. */
  disponivel: boolean
  motivo: string | null
  configuradas: string[]
  /** Nomes configurados que o ATHUS não tem: a importação os recusaria. */
  nao_achadas: string[]
  categorias: CategoriaDoAthus[]
}

// --- a conferencia mensal ---------------------------------------------------

export type ItemDeConferenciaNaTela = {
  id: number
  chave: string
  rotulo: string
  /** O dispositivo ou manual de onde o item veio. Item sem isto nao entra. */
  fundamento: string | null
  situacao: string
  observacao: string | null
  conferido_em: string | null
}

export type ConferenciaDoMes = {
  competencia: string
  itens: ItemDeConferenciaNaTela[]
  pendentes: number
}

// --- a regularidade do fornecedor -------------------------------------------

export type LinhaDeRegularidade = {
  tipo: string
  nome: string
  /** `valida` / `vencida` NAQUELA data, ou `sem_registro`. */
  situacao: string
  numero: string | null
  emissao: string | null
  validade: string | null
  dias_para_vencer: number | null
  onde_obter: string | null
  observacao: string | null
}

export type RegularidadeDoContrato = {
  contratado: string | null
  documento: string | null
  /** A data em que a validade foi aferida — NAO e sempre hoje. */
  em: string
  linhas: LinhaDeRegularidade[]
  vencidas: number
  a_vencer: number
  sem_registro: number
}

export type ValidacaoNaTela = {
  tipo: string
  nome: string
  /** `confirmada`, `negada`, `divergente`, `nao_configurado`, `indisponivel`. */
  situacao: string
  fonte: string
  consultado_em: string
  mensagem: string
  o_que_fazer: string
  validade_no_orgao: string | null
  validade_registrada: string | null
  divergencias: string[]
  link_do_orgao: string | null
  credencial_que_falta: string | null
}

// --- configuracoes ----------------------------------------------------------

export type ContaNaTela = {
  id: number
  nome: string
  email: string
  /** O perfil de maior alcance; a lista completa está em `perfis` (spec 0070). */
  papel: string
  perfis: string[]
  ativo: boolean
  is_root: boolean
  entidade: string | null
  /** Verdadeiro enquanto outra pessoa conhece a senha desta conta. */
  senha_provisoria: boolean
  senha_alterada_em: string | null
  /** F41: na lista o CPF vem SEMPRE mascarado, inclusive para o gestor. */
  cpf_mascarado?: string | null
  matricula?: string | null
  cargo?: string | null
}

export type PapelNaTela = {
  papel: string
  nome: string
  /** O que este papel PODE — escrito, para quem escolhe nao adivinhar. */
  pode: string
}

export type RespostaDasContas = {
  dados: ContaNaTela[]
  papeis: PapelNaTela[]
  pode_administrar: boolean
}

export type LinhaDaTrilhaNaTela = {
  id: number
  momento: string
  tabela: string
  registro_id: number
  operacao: string
  /** "criou", "alterou", "entrou", "entrada recusada", "consultou". */
  operacao_rotulo: string
  campo: string | null
  valor_anterior: string | null
  valor_novo: string | null
  autor_id: number | null
  /** Copiado NO MOMENTO DO FATO: o rastro continua legivel depois. */
  autor_nome: string | null
  autor_email: string | null
  endereco_de_rede: string | null
  contexto: string | null
  ato: string
  campo_rotulo: string | null
  /** O que a linha tocou, em palavras (F42). `tabela` fica para o filtro. */
  tabela_rotulo: string
}

export type AutorNaTrilha = {
  autor_id: number | null
  nome: string
  quantas: number
}

export type FiltroDaTrilha = {
  pagina?: number
  por_pagina?: number
  autor_id?: number | null
  tabela?: string
  de?: string
  ate?: string
}

export type RespostaDaTrilha = {
  dados: LinhaDaTrilhaNaTela[]
  /** Linhas que casam com o filtro — nao o tamanho da pagina. */
  total: number
  total_sem_filtro: number
  pagina: number
  por_pagina: number
  /** MEDIDO: quantas rotas de escrita chamam o servico da trilha. Nunca a
   * contagem de linhas — entidade que nao praticou ato tem a trilha vazia e o
   * sistema gravando, e sao fatos diferentes. */
  registra: boolean
  explicacao: string
  autores: AutorNaTrilha[]
  tabelas: string[]
  tabelas_rotulos: Record<string, string>
}

/* --- o perfil (F37) -------------------------------------------------------- */

export type DesignacaoNoPerfil = {
  contrato_id: number
  numero: string
  ano: number
  objeto: string
  papel: string
  papel_rotulo: string
  ato_de_designacao: string | null
  inicio: string
  fim: string | null
  vigente: boolean
}

export type AtoNoPerfil = {
  id: number
  momento: string
  operacao: string
  operacao_rotulo: string
  tabela: string
  registro_id: number
  campo: string | null
  valor_novo: string | null
  contexto: string | null
  /** O ato em palavras — é o que a tela mostra. O contexto fica para o TI. */
  ato: string
  campo_rotulo: string | null
}

export type Perfil = {
  id: number
  nome: string
  email: string
  papel: string
  papel_nome: string
  papel_pode: string
  is_root: boolean
  entidade: string | null
  entidade_nome: string | null

  telefone: string | null
  avisar_por_email: boolean
  avisar_por_whatsapp: boolean

  senha_provisoria: boolean
  senha_alterada_em: string | null
  politica_da_senha: string

  designacoes: DesignacaoNoPerfil[]
  atos: AtoNoPerfil[]
  atos_totais: number

  /* --- F41: a identificação funcional ----------------------------------- */
  entidade_id: number | null
  /** Inteiro: quem pergunta é o próprio dono. */
  cpf: string | null
  matricula: string | null
  cargo: string | null
  funcao: string | null
  lotacao: string | null
  vinculo: string | null
  vinculo_rotulo: string | null
  ato_de_nomeacao: string | null
  ato_de_nomeacao_data: string | null
  tem_foto: boolean
  foto_enviada_em: string | null
  /** O tamanho máximo da foto nesta entidade; nulo quando não há limite (spec 0120). */
  foto_limite_kb?: number | null
  /** Verdadeiro quando passar do limite recusa a foto; falso quando só avisa. */
  foto_limite_bloqueia?: boolean
  vinculos: Opcao[]

  /* --- spec 0072: a pessoa no padrão DATAFull ---------------------------- */
  perfis: string[]
  perfis_na_tela: PerfilNaTela[]
  /** As entidades em que a pessoa tem vínculo ativo; a da sessão é `entidade_id`. */
  entidades: EntidadeDaSessao[]
  /** O cargo e o setor do vínculo nesta entidade, para as listas. */
  cargo_id: number | null
  setor_id: number | null
}

/* --- F41: entidades, licenciamento e perfil completo ------------------------ */

export type Opcao = { chave: string; rotulo: string }

/** Campo enviado vazio apaga; campo não enviado não muda. */
export type MudancaDoCadastro = Record<string, string | null>

export type RespostaDaImagem = { tipo: string; tamanho: number; alerta: string | null }

export type EntradaNoPerfil = {
  momento: string
  recusada: boolean
  motivo: string | null
  endereco_de_rede: string | null
  com_senha_provisoria: boolean
}

export type RespostaDasEntradas = {
  dados: EntradaNoPerfil[]
  total: number
  recusadas: number
}

export type DadosFuncionaisDaConta = {
  id: number
  nome: string
  email: string
  cpf: string | null
  matricula: string | null
  cargo: string | null
  funcao: string | null
  lotacao: string | null
  vinculo: string | null
  ato_de_nomeacao: string | null
  ato_de_nomeacao_data: string | null
  telefone: string | null
  /** O que está escolhido nas listas, e a entidade do vínculo (spec 0072). */
  entidade_id: number | null
  cargo_id: number | null
  setor_id: number | null
}

/** Um setor da entidade, para a escolha da lotação (spec 0072). */
export type SetorNaTela = { id: number; nome: string; sigla: string | null; caminho: string }

export type TipoDeEntidadeNoCadastro = { id: number; nome: string; ativo: boolean; entidades: number }
export type RespostaDosTiposDeEntidade = { dados: TipoDeEntidadeNoCadastro[]; pode_alterar: boolean }
export type CargoNoCadastro = { id: number; nome: string; ativo: boolean; contas: number }
export type RespostaDosCargos = { dados: CargoNoCadastro[]; pode_alterar: boolean }

export type SetorNoCadastro = {
  id: number
  nome: string
  sigla: string | null
  superior_id: number | null
  tipo_id: number | null
  tipo: string | null
  ativo: boolean
  contas: number
  caminho: string
}
export type RespostaDosSetores = { entidade_id: number; dados: SetorNoCadastro[]; pode_alterar: boolean }
export type PedidoDeSetor = {
  entidade_id?: number
  nome: string
  sigla: string | null
  tipo_id: number | null
  superior_id: number | null
}
export type MudancaDeSetor = Partial<Omit<PedidoDeSetor, "entidade_id">> & { ativo?: boolean }
export type CargoNaTela = { id: number; nome: string }

export type EntidadeNaLista = {
  id: number
  nome: string
  sigla: string
  nome_exibicao: string | null
  cnpj: string | null
  tipo_rotulo: string | null
  municipio: string | null
  uf: string | null
  ativa: boolean
  licenca_modalidade_rotulo: string | null
  licenca_fim: string | null
  is_demo: boolean
  brasao_enviado_em: string | null
  sistemas: string[]
}

export type RespostaDasEntidades = {
  dados: EntidadeNaLista[]
  total: number
  pode_criar: boolean
  tipos: Opcao[]
  poderes: Opcao[]
  esferas: Opcao[]
}

export type SistemaDaEntidadeNaTela = {
  sistema_id: number
  sigla: string
  nome: string
  descricao: string | null
  situacao: string
  situacao_rotulo: string
  data: string | null
  observacao: string | null
}

export type SistemaNoPedido = {
  sistema_id: number
  situacao: string
  data: string | null
  observacao: string | null
}

export type SistemaDoCatalogo = {
  id: number
  sigla: string
  nome: string
  descricao: string | null
  fonte: string | null
  ordem: number
}

export type CanalDoAthus = {
  canal: string
  rotulo: string
  configurado: boolean
  falta: string[]
  /** Este canal é a fonte da importação (ATHUS_FONTE). */
  usado: boolean
}

export type FichaDaEntidade = {
  id: number
  nome: string
  sigla: string
  cnpj: string | null
  nome_exibicao: string | null
  tipo: string | null
  poder: string | null
  esfera: string | null
  natureza_juridica: string | null
  ativa: boolean
  is_demo: boolean
  municipio: string | null
  uf: string | null
  codigo_ibge: string | null
  logradouro: string | null
  numero_do_endereco: string | null
  complemento: string | null
  bairro: string | null
  cep: string | null
  telefone: string | null
  email_institucional: string | null
  site: string | null
  representante_nome: string | null
  representante_cargo: string | null
  /** O contato do responsável, da DDL DATAFull (spec 0071). */
  responsavel_email: string | null
  responsavel_telefone: string | null
  representante_cpf: string | null
  representante_cpf_mascarado: boolean
  representante_mandato_inicio: string | null
  representante_mandato_fim: string | null
  tce_codigo_da_unidade: string | null
  tce_nome: string | null
  brasao: { existe: boolean; tipo: string | null; tamanho: number | null; enviado_em: string | null }
  licenca_modalidade: string | null
  licenca_contrato: string | null
  licenca_inicio: string | null
  licenca_fim: string | null
  licenca_observacao: string | null
  licenca_situacao: string | null
  licenca_dias_para_o_fim: number | null
  sistemas: SistemaDaEntidadeNaTela[]
  ligacao_com_o_athus: CanalDoAthus[]
  /** "banco" ou "api"; nulo quando a fonte não foi escolhida. */
  fonte_do_athus: string | null
  pode_editar_cadastro: boolean
  pode_editar_licenciamento: boolean
  pode_mudar_situacao: boolean
  desativada_em: string | null
  tipos: Opcao[]
  poderes: Opcao[]
  esferas: Opcao[]
  modalidades: Opcao[]
  situacoes_do_sistema: Opcao[]
}

export type PecaNaTela = {
  id: number
  cd_tipo_documento: number
  tipo_descricao: string | null
  papel: string
  titulo: string | null
  numero: string | null
  ano: number | null
  criado_na_origem_em: string | null
  cancelada: boolean
  qtd_assinados: number | null
  /** Nulo = a fonte não informou (a API do ATHUS não distingue juntada). */
  juntada: boolean | null
  origem_documento_id: string | null
  resumo_sha256: string | null
  tamanho_bytes: number | null
  pdf_conferido_em: string | null
  /** De onde veio o arquivo que o fiscal viu da última vez. Nulo = ninguém
   *  abriu ainda, que é a mesma verdade de "pdf_conferido_em" nulo. */
  pdf_origem: FonteDoArquivo | null
  /** A palavra que a tela mostra, vinda do servidor — o vocabulário do
   *  domínio mora num lugar só. */
  pdf_origem_rotulo: string | null
}

export type GrupoDePecas = { papel: string; rotulo: string; pecas: PecaNaTela[] }

export type PecasDoContrato = {
  contrato_id: number
  /** As peças DE FISCALIZAÇÃO — as `processo` não entram nos grupos. */
  total: number
  /** O processo INTEIRO. É o número que leva o fiscal a abrir a esteira. */
  total_no_processo: number
  canceladas: number
  grupos: GrupoDePecas[]
  sincronizado_em: string | null
}

/**
 * O que o sistema tem a dizer sobre a peça além do que o ATHUS diz: a ÚLTIMA
 * avaliação do fiscal. Lista vazia é a verdade "nada foi apontado".
 */
export type MarcaNaEsteira = {
  /** `validada`, `recusada`, `com_ressalva` ou `anotacao`. */
  tipo: string
  rotulo: string
  texto: string | null
  por: string | null
  em: string | null
  laudo_id: number | null
}

export type EntradaDoProcesso = {
  id: number
  /** A posição cronológica. NULA quando o contrato ainda não foi carregado
   *  desde a F27 — a tela mostra travessão, nunca zero. */
  ordem: number | null
  /** `peca` (do ATHUS) ou `laudo` (deste sistema). */
  tipo_entrada: string
  cd_tipo_documento: number
  tipo_descricao: string | null
  papel: string
  rotulo_do_papel: string
  no_catalogo: boolean
  titulo: string | null
  numero: string | null
  ano: number | null
  autor_nome: string | null
  data: string | null
  cancelada: boolean
  /** Nulo = a fonte não informou (a API do ATHUS não distingue juntada). */
  juntada: boolean | null
  qtd_assinados: number | null
  resumo_sha256: string | null
  tamanho_bytes: number | null
  pdf_conferido_em: string | null
  pdf_origem: FonteDoArquivo | null
  pdf_origem_rotulo: string | null
  /** O PDF já está em disco AGORA — não é o mesmo que `pdf_origem`, que diz de
   *  onde ele veio da última vez. A esteira usa isto para avisar, ANTES de a
   *  espera começar, que o documento vem ao vivo da API do ATHUS. */
  no_acervo_local: boolean
  marcas: MarcaNaEsteira[]
  /** Só na entrada de laudo — nulo na peça, que não tem assinatura nossa. */
  laudo_situacao: string | null
  laudo_nivel: string | null
  laudo_codigo: string | null
  laudo_remessa_detalhe: string | null
}

/**
 * A AVALIAÇÃO DO FISCAL sobre uma peça, e o LAUDO que ela produz (F29).
 *
 * `pdf` não vem aqui de propósito: o binário desce pela rota de arquivo,
 * quando alguém abre o laudo. Quarenta laudos em Base64 dentro da resposta da
 * esteira triplicariam o que a tela baixa para mostrar uma linha de texto.
 */
export type LaudoNaTela = {
  id: number
  numero: string
  tipo: string
  rotulo_do_tipo: string
  texto: string | null
  nivel: string
  situacao: string
  assinado_por_nome: string | null
  assinado_por_email: string | null
  assinado_em: string | null
  resumo_sha256: string
  tamanho_bytes: number
  codigo_de_autenticidade: string
  juntado_no_processo_em: string | null
  remessa_resultado: string | null
  remessa_detalhe: string | null
}

export type AvaliacaoNaTela = {
  id: number
  peca_id: number
  decisao: string
  rotulo_da_decisao: string
  texto: string | null
  /** O que a verificação apontou e o ato seguiu assim mesmo (LEI 1). */
  alerta_da_verificacao: string | null
  avaliado_por_nome: string | null
  avaliado_em: string
  laudo_id: number | null
}

export type RespostaDaAvaliacao = {
  avaliacao: AvaliacaoNaTela
  laudo: LaudoNaTela
  avisos_gerados: number
}

/** O que a página pública devolve — e ela não cresce: cada campo a mais aqui
 *  é uma decisão de divulgação. */
export type ConferenciaDeAutenticidade = {
  numero: string
  tipo: string
  rotulo_do_tipo: string
  entidade: string | null
  contrato_numero: string
  contrato_ano: number
  signatario_nome: string | null
  assinado_em: string | null
  nivel: string
  situacao: string
  resumo_sha256: string
  selo_confere: boolean
  is_demo: boolean
}

export type ProcessoDoContrato = {
  contrato_id: number
  processo_administrativo: string | null
  /** O total DO RECORTE pedido. O cabeçalho conta o processo inteiro em
   *  `no_catalogo + fora_do_catalogo`, e não encolhe com o filtro. */
  total: number
  no_catalogo: number
  fora_do_catalogo: number
  canceladas: number
  /** Os laudos do contrato inteiro; como os demais, não encolhe com o filtro. */
  laudos: number
  sincronizado_em: string | null
  entradas: EntradaDoProcesso[]
}

/**
 * DE ONDE VEIO O ARQUIVO que a tela está mostrando.
 *
 * O servidor manda no cabeçalho `X-Fonte-Do-Arquivo` e grava a mesma palavra
 * na peça. A tela a mostra porque a pergunta "estou olhando o documento do
 * processo ou uma cópia guardada aqui?" é uma pergunta de fiscalização, e não
 * um detalhe de implementação.
 */
export type FonteDoArquivo = "acervo_local" | "athus" | "demonstracao"

/** A palavra que a tela usa para cada fonte — a de quem fiscaliza, e não a do
 *  código. "acervo local" saiu em 09/09/2026: era o nome que este sistema dá à
 *  pasta em disco, e ninguém que fiscaliza contrato usa essa palavra. */
export const ROTULO_DA_FONTE: Record<FonteDoArquivo, string> = {
  acervo_local: "cópia guardada neste sistema",
  athus: "ATHUS",
  demonstracao: "documento de demonstração",
}

function fonteDoCabecalho(resposta: Response): FonteDoArquivo | null {
  const bruta = resposta.headers.get("X-Fonte-Do-Arquivo")
  // Fonte desconhecida vira NULA, e a tela não mostra nada — melhor que
  // inventar um rótulo para uma palavra que este código não conhece.
  return bruta !== null && bruta in ROTULO_DA_FONTE ? (bruta as FonteDoArquivo) : null
}

/** O que uma busca de arquivo devolve.
 *
 * `arquivo` é o binário em si, e ele vem junto do endereço porque a esteira
 * LEMBRA das peças já abertas: guardar o endereço não serviria — ele é
 * revogado ao trocar de peça —, e guardar o `Blob` deixa a volta custar um
 * `createObjectURL`, que é memória, e não rede. */
export type ArquivoBaixado = {
  arquivo: Blob
  endereco: string
  tamanho: number
  fonte: FonteDoArquivo | null
}

/** O pedido foi CANCELADO por quem o fez, e isso não é falha.
 *
 * Sem esta distinção, trocar de peça pintaria um bloco vermelho de erro na
 * tela a cada clique em "próxima" — e o fiscal aprenderia a ignorar o bloco
 * vermelho, que é o pior resultado possível de um aviso. */
export function foiAbortado(falha: unknown): boolean {
  return falha instanceof DOMException && falha.name === "AbortError"
}

/**
 * BUSCA O PDF DA PEÇA e devolve um endereço temporário de blob.
 *
 * Não é um `<a href>`: a rota exige a credencial no cabeçalho, e um link não a
 * leva. Busca-se o binário com a credencial e devolve-se o endereço do que
 * voltou. O servidor grava o resumo SHA-256 nesse momento — a prova de que o
 * que foi visto é o que está na origem.
 *
 * QUEM CHAMA É QUEM SOLTA. `URL.revokeObjectURL` fica com o chamador porque só
 * ele sabe quando parou de usar: a esteira solta ao trocar de peça; a aba nova
 * solta depois de um minuto, quando o navegador já leu.
 *
 * `sinal` EXISTE PORQUE DESISTIR PRECISA CHEGAR À REDE — 04/09/2026
 * -----------------------------------------------------------------
 * A esteira trocava de peça e marcava a anterior como "não sirvo mais", mas a
 * requisição continuava correndo. Clicar "próxima" seis vezes deixava vários
 * megabytes em voo; o navegador satura as conexões para o mesmo endereço, e o
 * visor da peça que interessa fica em "Abrindo o documento…" para sempre. Foi
 * o que DEV fotografou e chamou de travamento.
 *
 * Quem aborta recebe um `DOMException` de nome `AbortError`, e ele NÃO é
 * falha: é o pedido tendo sido cancelado por quem o fez. Ver `foiAbortado`.
 */
export async function baixarPeca(
  contratoId: number,
  pecaId: number,
  sinal?: AbortSignal,
): Promise<ArquivoBaixado> {
  const credencial = credencialGuardada()
  const cabecalhos: Record<string, string> = {}
  if (credencial !== null) cabecalhos["Authorization"] = `Bearer ${credencial}`

  const resposta = await fetch(`/api/contratos/${contratoId}/pecas/${pecaId}/arquivo`, {
    headers: cabecalhos,
    signal: sinal,
  })
  if (!resposta.ok) {
    let corpo: unknown = null
    try {
      corpo = await resposta.json()
    } catch {
      corpo = null
    }
    if (resposta.status === 401) {
      esquecerSessao()
      window.dispatchEvent(new CustomEvent("sessao-expirada"))
    }
    throw new ErroDaApi(resposta.status, (corpo as Record<string, unknown>) ?? {})
  }
  const binario = await resposta.blob()
  return {
    arquivo: binario,
    endereco: URL.createObjectURL(binario),
    tamanho: binario.size,
    fonte: fonteDoCabecalho(resposta),
  }
}

/** ABRE O PDF DA PEÇA numa aba nova — o caminho de sempre, para quem quer o
 *  documento fora do sistema (imprimir, anexar a um e-mail). */
export async function abrirPeca(contratoId: number, pecaId: number): Promise<void> {
  const { endereco } = await baixarPeca(contratoId, pecaId)
  window.open(endereco, "_blank", "noopener")
  // O navegador já tem o binário; o endereço temporário pode ir embora.
  window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000)
}

/**
 * BUSCA O PDF DO LAUDO e devolve um endereço temporário de blob.
 *
 * O mesmo desenho de `baixarPeca`, e pela mesma razão: a rota exige a
 * credencial no cabeçalho, e um link não a leva. Quem chama é quem solta o
 * endereço.
 */
export async function baixarLaudo(
  contratoId: number,
  laudoId: number,
  sinal?: AbortSignal,
): Promise<ArquivoBaixado> {
  const credencial = credencialGuardada()
  const cabecalhos: Record<string, string> = {}
  if (credencial !== null) cabecalhos["Authorization"] = `Bearer ${credencial}`

  const resposta = await fetch(
    `/api/contratos/${contratoId}/laudos/${laudoId}/arquivo`,
    { headers: cabecalhos, signal: sinal },
  )
  if (!resposta.ok) {
    let corpo: unknown = null
    try {
      corpo = await resposta.json()
    } catch {
      corpo = null
    }
    if (resposta.status === 401) {
      esquecerSessao()
      window.dispatchEvent(new CustomEvent("sessao-expirada"))
    }
    throw new ErroDaApi(resposta.status, (corpo as Record<string, unknown>) ?? {})
  }
  const binario = await resposta.blob()
  // `fonte` sai NULA, e é a verdade: o laudo não vem de fonte nenhuma — ele
  // nasceu aqui. A pergunta "de onde veio este arquivo" só existe para a peça
  // do processo, que é documento de outro sistema.
  return {
    arquivo: binario,
    endereco: URL.createObjectURL(binario),
    tamanho: binario.size,
    fonte: null,
  }
}

/** ABRE O PDF DO LAUDO numa aba nova — para imprimir, ou juntar a um e-mail. */
export async function abrirLaudo(contratoId: number, laudoId: number): Promise<void> {
  const { endereco } = await baixarLaudo(contratoId, laudoId)
  window.open(endereco, "_blank", "noopener")
  window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000)
}

export type MemoriaNaTela = {
  id: number
  valida: boolean
  regra: string
  marco: string | null
  data_marco: string | null
  prazo_quantidade: number | null
  prazo_unidade: string | null
  data_inicio: string | null
  data_juridica: string | null
  data_preventiva: string | null
  data_operacional: string | null
  nivel: string
  estado: string
  exige_validacao: boolean
  fonte: string | null
  evidencia: string | null
  explicacao: string
  anomalias: unknown[] | null
  parametro_procedencia: string | null
  calculada_em: string
  validado_por_id: number | null
  validado_em: string | null
  motivo_da_alteracao: string | null
  /** A regra e o nível em palavras. `regra` e `nivel` são o código, que a tela não mostra. */
  regra_rotulo: string
  nivel_rotulo: string
}

export type EventoNaTela = {
  id: number
  tipo: string
  data: string
  origem: string
  fonte: string | null
  evidencia: string | null
  peca_id: number | null
  validado_em: string | null
  motivo: string | null
  veiculo: string | null
  referencia: string | null
  pagina: number | null
}

export type ProvidenciaNaTela = {
  id: number
  faixa_dias: number
  descricao: string
  responsavel: string
  escalonamento: string | null
  prazo: string
  referencia_preventiva: boolean
  situacao: string
  concluida_em: string | null
  observacao: string | null
  atrasada: boolean
}

export type VigenciaDoContrato = {
  contrato_id: number
  criticidade: string | null
  memoria: MemoriaNaTela | null
  pendente_de_decisao: MemoriaNaTela | null
  historico: MemoriaNaTela[]
  eventos: EventoNaTela[]
  providencias: ProvidenciaNaTela[]
  /** As datas declaradas deste contrato, na ordem em que aconteceram. Nada se apaga. */
  declaracoes: DeclaracaoNaTela[]
  /** Quem pede responde por este contrato: fiscal designado, gestor, plataforma. */
  pode_agir: boolean
}

export type DeclaracaoNaTela = {
  id: number
  chave: string
  rotulo: string
  data: string
  data_anterior: string | null
  estado: string
  /** "vale", "revista" ou "retirada". */
  situacao: string
  motivo: string | null
  sem_motivo: boolean
  peca_id: number | null
  quem: string | null
  papel: string
  declarada_em: string
  retirada_em: string | null
  retirada_por: string | null
  motivo_da_retirada: string | null
  divergencia_data: string | null
  divergencia_fonte: string | null
}

/**
 * UMA DAS SEIS DATAS DO CONTRATO.
 *
 * `estado` diz COMO ela chegou aqui, e a diferenca decide o que o fiscal faz:
 * `validada` ele usa; `extraida` ele confere no PDF (e `peca_id` o leva ate
 * ele); `cadastrada` ele confere com quem digitou; `ausente` ele vai buscar.
 *
 * As seis vem SEMPRE, inclusive as ausentes — a linha vazia diz "o sistema
 * procurou e nao ha", que e diferente de "o sistema nao olhou".
 */
export type DataNaFicha = {
  chave: string
  rotulo: string
  data: string | null
  fonte: string | null
  estado: string
  peca_id: number | null
  evidencia: string | null
  /** Por que a data nao esta ai. Campo vazio e travessao COM O MOTIVO. */
  motivo_da_ausencia: string | null
  /** So a publicacao os traz: sem eles a data nao se confere em lugar nenhum. */
  veiculo: string | null
  referencia: string | null
  /** A data que VALE porque foi declarada. Nula = vale a proposta do sistema. */
  declaracao: DeclaracaoNaFicha | null
  /** Todas as datas que o sistema encontrou, uma por dia, com as origens. */
  encontradas: DataEncontrada[]
}

export type DataEncontrada = {
  data: string
  origens: string[]
  peca_id: number | null
  /** A que o sistema usaria sozinho. */
  em_uso: boolean
}

export type DeclaracaoNaFicha = {
  id: number
  data: string
  quem: string
  motivo: string | null
  sem_motivo: boolean
  peca_id: number | null
  proposta_do_sistema: string | null
  divergencia_data: string | null
  divergencia_fonte: string | null
}

/**
 * A PECA DE PUBLICIDADE JUNTADA — o fato de tramitacao, com o nome dele.
 *
 * Ate 04/09/2026 esta data aparecia na ficha sob o rotulo "Publicacao", e o
 * motor contava vigencia dela. Ela continua na tela, logo abaixo da linha da
 * publicacao, porque mostra que a providencia foi tomada — e agora diz o que e.
 */
export type PublicidadeJuntadaNaFicha = {
  peca_id: number
  quando: string
  tipo: string | null
  documento_id: string | null
  quantas: number
}

export type PrazoNaFicha = {
  quantidade: number
  unidade: string
  fonte: string | null
  trecho: string | null
  pagina: number | null
}

export type OrigemDoPrazoNaFicha = {
  marco: string | null
  marco_rotulo: string | null
  data_do_marco: string | null
  por_que_este_marco: string | null
  prazo: PrazoNaFicha | null
  regra: string | null
  regra_rotulo: string | null
  termino_juridico: string | null
  data_preventiva: string | null
  nivel: string | null
  nivel_rotulo: string | null
  estado: string | null
  exige_validacao: boolean
  narrativa: string
  o_que_falta: string[]
  termino_declarado_por: string | null
  marco_declarado_por: string | null
  /** Datas que se contradizem, em palavras: aparecem como problema, nao como prazo. */
  problemas: string[]
  /** A cláusula de vigência sem data nem prazo, para o fiscal declarar o fim (spec 0098). */
  clausula_sem_prazo?: { trecho: string; pagina: number | null } | null
}

export type DatasDoContrato = {
  contrato_id: number
  datas: DataNaFicha[]
  origem_do_prazo: OrigemDoPrazoNaFicha
  publicidade_juntada: PublicidadeJuntadaNaFicha | null
  pode_corrigir: boolean
  aviso: string | null
}

export type AvisoNaTela = {
  id: number
  tipo: string
  gravidade: string
  contrato_id: number | null
  contrato_numero: string | null
  assunto: string
  corpo: string
  criado_em: string
  lida_em: string | null
}

export type MinhaConta = {
  id: number
  nome: string
  email: string
  papel: string
  telefone: string | null
  avisar_por_email: boolean
  avisar_por_whatsapp: boolean
}

export type SituacaoDosAvisos = {
  canais: { canal: string; configurado: boolean; o_que_falta: string[]; caixa_de_teste: string | null }[]
  por_resultado: Record<string, number>
  ultima_execucao: string | null
  ultima_execucao_falhou: boolean
  todas_as_entidades: boolean
  pode_reiniciar: boolean
}

export type ResultadoDoProcessamento = {
  avisos_novos: number
  enviadas: number
  falhas: number
  nao_configuradas: number
  sem_destino: number
}

export type ResultadoDoTeste = {
  canal: string
  enviado: boolean
  resultado: string
  detalhe: string | null
  destino: string | null
}

export type FaixaDeVencimento = { dias: number; contratos: number; valor: string | null; sem_valor: number }

export type ContratoCritico = {
  contrato_id: number
  numero: string
  objeto: string
  valor: string | null
  fim: string | null
  dias: number | null
  preventiva: boolean
  nivel: string | null
}

export type PainelExecutivo = {
  ativos: number
  ativos_valor: string | null
  ativos_sem_valor: number
  vigentes: number
  vencidos_em_execucao: number
  vencidos_valor: string | null
  vencimentos: FaixaDeVencimento[]
  sem_definicao_segura: number
  sem_data_nenhuma: number
  sem_providencia_de_continuidade: number
  valor_exposto_a_risco: string | null
  criticos_por_valor: ContratoCritico[]
  por_situacao: Record<string, number>
  por_modalidade: Record<string, number>
  por_tipo: Record<string, number>
  por_nivel: Record<string, number>
  por_entidade: Record<string, number>
  providencias: Record<string, number>
  avisos_30_dias: Record<string, number>
  pecas_por_papel: Record<string, number>
  faixas_exclusivas: LinhaDoTempo["faixas"]
  com_data: number
  sem_data: number
  faixa_da_continuidade: number
  atraso_das_providencias: { medio_dias: number | null; maior_dias: number | null }
  novos_no_mes: number
  sem_data_de_inicio: number
  canais_configurados: boolean
}

export type ItemOperacional = {
  contrato_id: number
  contrato_numero: string
  contrato_objeto: string
  o_que: string
  prazo: string | null
  dias: number | null
  detalhe: string | null
  responsavel: string | null
  preventiva: boolean
}

export type PainelOperacional = {
  meus_contratos: number
  providencias_hoje: ItemOperacional[]
  prazos_atrasados: ItemOperacional[]
  aguardando_manifestacao: ItemOperacional[]
  conflitos_documentais: ItemOperacional[]
  documentos_a_vencer: ItemOperacional[]
}

export type RelatorioDisponivel = { nome: string; titulo: string; descricao: string }

/** Baixa um relatório CSV com a credencial no cabeçalho (link simples não a leva). */
export async function baixarRelatorio(nome: string): Promise<void> {
  const credencial = credencialGuardada()
  const cabecalhos: Record<string, string> = {}
  if (credencial !== null) cabecalhos["Authorization"] = `Bearer ${credencial}`
  const resposta = await fetch(`/api/relatorios/${nome}`, { headers: cabecalhos })
  if (!resposta.ok) {
    let corpo: unknown = null
    try {
      corpo = await resposta.json()
    } catch {
      corpo = null
    }
    throw new ErroDaApi(resposta.status, (corpo as Record<string, unknown>) ?? {})
  }
  const binario = await resposta.blob()
  const endereco = URL.createObjectURL(binario)
  const ancora = document.createElement("a")
  ancora.href = endereco
  const hoje = new Date()
  const dois = (n: number) => String(n).padStart(2, "0")
  ancora.download = `${nome}-${hoje.getFullYear()}-${dois(hoje.getMonth() + 1)}-${dois(hoje.getDate())}.csv`
  document.body.appendChild(ancora)
  ancora.click()
  ancora.remove()
  window.setTimeout(() => URL.revokeObjectURL(endereco), 60_000)
}

/* --- F42: Configurações em áreas ------------------------------------------ */

export type ItemAlcancado = { chave: string; area: string; ve: boolean; edita: boolean }

/** O que a conta alcança em Configurações. É a MESMA tabela que o servidor
 *  usa para recusar: a tela não decide quem vê o quê. */
export type AlcanceDeConfiguracoes = {
  itens: ItemAlcancado[]
  administra_todas: boolean
  conta_de_demonstracao: boolean
  conta_de_plataforma: boolean
}

export type VigenciaNaTela = {
  id: number
  valor: string
  comportamento: string
  comportamento_em_palavras: string
  fundamento: string | null
  descricao: string | null
  vigencia_inicio: string
  vigencia_fim: string | null
  entidade_id: number | null
  vale_para: string
  vale_hoje: boolean
}

export type RegraNaTela = {
  chave: string
  nome: string
  unidade: string | null
  assunto: string
  vigente: VigenciaNaTela | null
  historico: VigenciaNaTela[]
  pode_alterar_geral: boolean
  pode_alterar_da_entidade: boolean
}

export type RespostaDasRegras = {
  assuntos: string[]
  dados: RegraNaTela[]
  entidade_id: number | null
  comportamentos: Record<string, string>
}

export type PedidoDeVigencia = {
  valor: string
  comportamento: string
  vigencia_inicio: string
  entidade_id: number | null
  fundamento: string | null
  descricao: string | null
}

export type FaixaNaTela = {
  id: number
  dias: number
  providencia: string
  responsavel: string
  responsavel_rotulo: string
  escalonamento: string | null
  escalonamento_rotulo: string | null
  ativo: boolean
  entidade_id: number | null
  vale_para: string
  providencias_geradas: number
  pode_alterar: boolean
}

export type RespostaDasFaixas = {
  dados: FaixaNaTela[]
  responsaveis: Record<string, string>
  pode_criar_geral: boolean
  pode_criar_da_entidade: boolean
  entidade_id: number | null
}

export type PedidoDeFaixa = {
  dias: number
  providencia: string
  responsavel: string
  escalonamento: string | null
  entidade_id: number | null
}

export type TipoNaTela = {
  id: number
  codigo: number
  descricao: string
  papel: string
  papel_rotulo: string
  ativo: boolean
  observacao: string | null
  entidade_id: number | null
  vale_para: string
  pecas_no_sistema: number
  pode_alterar: boolean
}

export type RespostaDosTipos = { dados: TipoNaTela[]; papeis: Record<string, string> }

export type SistemaNoCatalogo = {
  id: number
  sigla: string
  nome: string
  descricao: string | null
  fonte: string | null
  ordem: number
  ativo: boolean
  entidades_com_o_sistema: number
}

export type TipoDeSetorNaTela = { id: number; tipo: string; ativo: boolean; setores: number }
export type RespostaDosTiposDeSetor = { dados: TipoDeSetorNaTela[]; pode_alterar: boolean }
export type RespostaDoCatalogo = { dados: SistemaNoCatalogo[]; pode_alterar: boolean }
